import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../utils/api';

/**
 * North Star 성장 지표 패널 (관리자 개요 상단).
 *  ① 활성 사용자수 — DAU(오늘) / MAU(30일), 검증 의사 비중
 *  ② 에이전트 참조 수 — AI 검색·어시스턴트·학습 봇 + 에이전트 API 호출 (봇별·경로별)
 * 잉크 미니멀: 회색조만, 색상 강조 없음.
 */

interface ActiveUsers {
  today: string;
  dau: { users: number; verified: number };
  mau: { since: string; users: number; verified: number };
  dailySeries: { day: string; users: number; verified: number }[];
}
interface AgentReferrals {
  since: string;
  until: string;
  total: number;
  byPurpose: { search: number; assistant: number; training: number; api: number };
  byBot: { bot: string; vendor: string; purpose: string; hits: number }[];
  topPaths: { path: string; hits: number }[];
  daily: { day: string; hits: number }[];
}

const PURPOSE_LABEL: Record<string, string> = {
  search: 'AI 검색(인용)',
  assistant: '어시스턴트 대행',
  training: '학습 크롤러',
  api: '에이전트 API',
};

const md = (day: string) => day.slice(5).replace('-', '/');

const GrowthPanel: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<{ activeUsers: ActiveUsers; agentReferrals: AgentReferrals } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    adminAPI
      .getGrowth(days)
      .then((r) => alive && setData(r.data))
      .catch((e: any) => alive && setError(e?.response?.data?.error || '성장 지표를 불러오지 못했습니다.'));
    return () => {
      alive = false;
    };
  }, [days]);

  if (error) return <div className="border border-gray-200 rounded-xl p-4 text-sm text-gray-500">{error}</div>;
  if (!data) return null;

  const { activeUsers: au, agentReferrals: ar } = data;
  const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const last14 = au.dailySeries.slice(-14);
  const ref14 = ar.daily.slice(-14);
  const maxDau = Math.max(1, ...last14.map((d) => d.users));
  const maxRef = Math.max(1, ...ref14.map((d) => d.hits));

  return (
    <section className="bg-white rounded-xl border border-gray-900 p-6">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">North Star 성장 지표</h3>
          <p className="text-xs text-gray-500 mt-0.5">활성 사용자수 · 에이전트 참조 수 — 모든 개선의 우선순위 기준</p>
        </div>
        <div className="flex gap-1 text-xs">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2 py-1 rounded border ${days === d ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
            >
              {d}일
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ① 활성 사용자 */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">① 활성 사용자</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">DAU (오늘 {md(au.today)})</p>
              <p className="text-2xl font-bold text-gray-900">{au.dau.users}</p>
              <p className="text-xs text-gray-500">검증 의사 {au.dau.verified}명 · {pct(au.dau.verified, au.dau.users)}%</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">MAU (30일)</p>
              <p className="text-2xl font-bold text-gray-900">{au.mau.users}</p>
              <p className="text-xs text-gray-500">검증 의사 {au.mau.verified}명 · {pct(au.mau.verified, au.mau.users)}%</p>
            </div>
          </div>
          <div className="flex items-end gap-0.5 h-14" title="최근 14일 DAU">
            {last14.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col justify-end" title={`${d.day}: ${d.users}명 (검증 ${d.verified})`}>
                <div className="bg-gray-900 rounded-sm" style={{ height: `${Math.max(2, (d.users / maxDau) * 100)}%` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>{md(last14[0]?.day || '')}</span>
            <span>DAU 14일</span>
            <span>{md(last14[last14.length - 1]?.day || '')}</span>
          </div>
        </div>

        {/* ② 에이전트 참조 */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">② 에이전트 참조 ({ar.since.slice(5)} ~ {ar.until.slice(5)})</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">총 참조</p>
              <p className="text-2xl font-bold text-gray-900">{ar.total}</p>
              <p className="text-xs text-gray-500">봇 {ar.byBot.length}종</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 text-xs text-gray-700 space-y-0.5">
              {(['search', 'assistant', 'api', 'training'] as const).map((k) => (
                <div key={k} className="flex justify-between">
                  <span className="text-gray-500">{PURPOSE_LABEL[k]}</span>
                  <span className="font-medium text-gray-900">{ar.byPurpose[k]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-end gap-0.5 h-14" title="최근 14일 에이전트 참조">
            {ref14.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col justify-end" title={`${d.day}: ${d.hits}건`}>
                <div className="bg-gray-400 rounded-sm" style={{ height: `${Math.max(2, (d.hits / maxRef) * 100)}%` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>{md(ref14[0]?.day || '')}</span>
            <span>참조 14일</span>
            <span>{md(ref14[ref14.length - 1]?.day || '')}</span>
          </div>
        </div>
      </div>

      {/* 봇별 · 경로별 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">봇별 상위</p>
          {ar.byBot.length === 0 ? (
            <p className="text-sm text-gray-400">아직 기록된 에이전트 참조가 없습니다.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {ar.byBot.slice(0, 8).map((b) => (
                  <tr key={`${b.bot}-${b.purpose}`}>
                    <td className="py-1 text-gray-900">{b.bot}</td>
                    <td className="py-1 text-gray-500 text-xs">{b.vendor} · {PURPOSE_LABEL[b.purpose] || b.purpose}</td>
                    <td className="py-1 text-right font-medium text-gray-900">{b.hits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">경로별 상위</p>
          {ar.topPaths.length === 0 ? (
            <p className="text-sm text-gray-400">—</p>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {ar.topPaths.slice(0, 8).map((p) => (
                  <tr key={p.path}>
                    <td className="py-1 text-gray-900 font-mono text-xs truncate max-w-[16rem]">{p.path}</td>
                    <td className="py-1 text-right font-medium text-gray-900">{p.hits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
};

export default GrowthPanel;
